import {
  formatOpeningHoursDayRange,
  getOpeningHoursDay,
  hasOpeningHoursData,
  type OpeningHoursShape,
} from '@/lib/openingHours';
import { openStoreDirections } from '@/lib/storeMapUrls';

export const WEEK_DAYS = [
  { key: 'monday', label: 'Segunda', short: 'Seg' },
  { key: 'tuesday', label: 'Terça', short: 'Ter' },
  { key: 'wednesday', label: 'Quarta', short: 'Qua' },
  { key: 'thursday', label: 'Quinta', short: 'Qui' },
  { key: 'friday', label: 'Sexta', short: 'Sex' },
  { key: 'saturday', label: 'Sábado', short: 'Sáb' },
  { key: 'sunday', label: 'Domingo', short: 'Dom' },
] as const;

const DAY_KEYS_BY_INDEX = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;

export type DayKey = (typeof WEEK_DAYS)[number]['key'];

export type DaySchedule = {
  key: DayKey;
  label: string;
  short: string;
  hours: string;
  isClosed: boolean;
  isToday: boolean;
};

export type StoreStatus = {
  isOpen: boolean;
  label: string;
  detail: string;
};

export type ContactStore = {
  _id?: string;
  company?: string;
  frontage?: string;
  phone?: { ios?: string; formatted?: string };
  whatsapp?: { ios?: string; formatted?: string };
  place?: { address?: string; loc?: number[] };
  openingHours?: OpeningHoursShape;
};

const parseTimeToMinutes = (time: string) => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + (minutes || 0);
};

const getCurrentDayKey = (): DayKey => DAY_KEYS_BY_INDEX[new Date().getDay()] as DayKey;

const getDayHours = (openingHours: ContactStore['openingHours'], dayKey: string) =>
  getOpeningHoursDay(openingHours, dayKey);

export function getStoreStatus(openingHours: ContactStore['openingHours']): StoreStatus {
  const todayKey = getCurrentDayKey();
  const todayHours = getDayHours(openingHours, todayKey);
  const currentMinutes = new Date().getHours() * 60 + new Date().getMinutes();

  if (todayHours?.from && todayHours?.to) {
    const from = parseTimeToMinutes(todayHours.from);
    const to = parseTimeToMinutes(todayHours.to);

    if (currentMinutes >= from && currentMinutes < to) {
      return { isOpen: true, label: 'Aberto agora', detail: `Até às ${todayHours.to}` };
    }
    if (currentMinutes < from) {
      return { isOpen: false, label: 'Fechado agora', detail: `Abrimos hoje às ${todayHours.from}` };
    }
  }

  const todayIndex = WEEK_DAYS.findIndex((d) => d.key === todayKey);
  for (let offset = 1; offset <= 7; offset += 1) {
    const nextDay = WEEK_DAYS[(todayIndex + offset) % 7];
    const nextHours = getDayHours(openingHours, nextDay.key);
    if (nextHours?.from && nextHours?.to) {
      const prefix = offset === 1 ? 'Amanhã' : nextDay.label;
      return { isOpen: false, label: 'Fechado agora', detail: `${prefix} às ${nextHours.from}` };
    }
  }

  return { isOpen: false, label: 'Fechado agora', detail: 'Veja os horários abaixo' };
}

export function formatOpeningHours(openingHours: ContactStore['openingHours']): DaySchedule[] {
  const todayKey = getCurrentDayKey();
  return WEEK_DAYS.map((day) => {
    const range = formatOpeningHoursDayRange(openingHours, day.key);
    const isClosed = range === 'Fechado';
    return {
      key: day.key,
      label: day.label,
      short: day.short,
      hours: isClosed ? 'Fechado' : range.replace(' - ', ' – '),
      isClosed,
      isToday: day.key === todayKey,
    };
  });
}

export { hasOpeningHoursData };

export function openMapsDirections(store: ContactStore) {
  openStoreDirections(store.place);
}
